import { Info, MoreHorizontal, Plus, RotateCcw, Star, Wrench } from "lucide-react";
import type { ReactNode } from "react";
import { Button } from "@/components/ui/button";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuLabel,
	DropdownMenuSeparator,
	DropdownMenuSub,
	DropdownMenuSubContent,
	DropdownMenuSubTrigger,
	DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
	addDeviceBlocker,
	connectionRemovable,
	deviceActionable,
	fixAgentBlocker,
	printerFixable,
	printerRemovable,
	removeDeviceBlocker,
	repairBlocker,
} from "./printer-labels";
import type { PlanAction, PlanNames } from "./printer-plan";
import {
	assignBlocker,
	type Installation,
	type PharmacyMap,
	type PrinterNode,
	type StationNode,
} from "./printer-topology";
import type {
	DeviceConnection,
	NetworkInfo,
	PharmacyPrintersResponse,
	PrinterDevice,
	PrinterItem,
} from "./printers-api";

/**
 * Akcje mapy: wszystko, co dziś było na kartach komputerów. Proste akcje
 * dopisują pozycję do planu od razu, akcje z wyborem (stanowisko, sterownik,
 * nowa domyślna) otwierają okno, które dopisuje ją po wyborze.
 */
export type MapActions = {
	plan: (action: PlanAction, names: PlanNames) => void;
	openShare: (host: PrinterDevice, printer: PrinterItem, clientDeviceUuid?: string) => void;
	openRemovePrinter: (device: PrinterDevice, printer: PrinterItem) => void;
	openRemoveConnection: (
		device: PrinterDevice,
		connection: DeviceConnection & { userName: string },
	) => void;
	openFix: (device: PrinterDevice, printer: PrinterItem) => void;
	openDefault: (device: PrinterDevice) => void;
	openAdd: (device: PrinterDevice, initialHost?: string) => void;
	openDetails: (device: PrinterDevice) => void;
};

export type MapContext = {
	view: PharmacyPrintersResponse;
	map: PharmacyMap;
	sharing: boolean;
	networks: ReadonlyMap<string, NetworkInfo | null>;
	actions: MapActions;
};

export function DisabledReason({ label, reason }: { label: string; reason: string }) {
	return (
		<DropdownMenuItem disabled className="flex-col items-start gap-0">
			<span>{label}</span>
			<span className="text-[11px] whitespace-normal text-muted-foreground">{reason}</span>
		</DropdownMenuItem>
	);
}

/** Akcje jednej instalacji drukarki (kolejka albo połączenie na komputerze). */
function InstallationItems({
	node,
	installation,
	ctx,
}: {
	node: PrinterNode;
	installation: Installation;
	ctx: MapContext;
}) {
	const { device, printer, connection } = installation;
	const { actions, view, sharing } = ctx;
	const items: ReactNode[] = [];
	const fixOk = device.online && device.fixSupported;
	const setDefaultItem = (printerName: string, label: string) =>
		items.push(
			<DropdownMenuItem
				key="default"
				onSelect={() =>
					actions.plan(
						{ kind: "set_default", input: { deviceUuid: device.deviceUuid, printerName } },
						{ device: device.displayName, printer: label },
					)
				}
			>
				<Star className="size-3.5" aria-hidden />
				Ustaw jako domyślną na {device.displayName}
			</DropdownMenuItem>,
		);

	if (printer) {
		if (fixOk && printerFixable(printer) && !printer.isDefault) {
			setDefaultItem(printer.name, printer.name);
		}
		if (fixOk && printerFixable(printer)) {
			items.push(
				<DropdownMenuItem key="fix" onSelect={() => actions.openFix(device, printer)}>
					<Wrench className="size-3.5" aria-hidden />
					Napraw…
				</DropdownMenuItem>,
			);
		}
		if (sharing && printer.managedShare && deviceActionable(device)) {
			const shareId = printer.managedShare.shareId;
			items.push(
				<DropdownMenuItem
					key="unshare"
					onSelect={() =>
						actions.plan(
							{ kind: "unshare", shareId },
							{ host: device.displayName, printer: printer.name },
						)
					}
				>
					Cofnij udostępnienie
				</DropdownMenuItem>,
			);
		}
		if (printerRemovable(printer)) {
			const reason = removeDeviceBlocker(device, view.removeMinAgentVersion);
			items.push(
				reason ? (
					<DisabledReason key="remove" label={`Usuń z ${device.displayName}…`} reason={reason} />
				) : (
					<DropdownMenuItem
						key="remove"
						variant="destructive"
						onSelect={() => actions.openRemovePrinter(device, printer)}
					>
						Usuń z {device.displayName}…
					</DropdownMenuItem>
				),
			);
		}
	}

	if (connection) {
		const label = connection.printerName ?? connection.path;
		if (fixOk && device.supported && !connection.isDefault) setDefaultItem(connection.path, label);
		if (sharing && connection.managed && connection.connectionId != null) {
			const connectionId = connection.connectionId;
			const forget = !deviceActionable(device);
			items.push(
				<DropdownMenuItem
					key="disconnect"
					variant="destructive"
					onSelect={() =>
						actions.plan(
							{ kind: "disconnect", connectionId, forget },
							{
								client: device.displayName,
								host: connection.hostDisplayName ?? undefined,
								printer: label,
							},
						)
					}
				>
					{forget ? `Zapomnij połączenie ${device.displayName}` : `Odłącz od ${device.displayName}`}
				</DropdownMenuItem>,
			);
			if (connection.addressStale || connection.byIp) {
				const host = connection.hostDeviceUuid
					? view.devices.find((d) => d.deviceUuid === connection.hostDeviceUuid)
					: undefined;
				const blocker = repairBlocker(device, host, false);
				items.push(
					blocker ? (
						<DisabledReason key="repair" label="Napraw połączenie" reason={blocker} />
					) : (
						<DropdownMenuItem
							key="repair"
							onSelect={() =>
								actions.plan(
									{ kind: "repair", connectionId },
									{
										client: device.displayName,
										host: connection.hostDisplayName ?? undefined,
										printer: label,
									},
								)
							}
						>
							<RotateCcw className="size-3.5" aria-hidden />
							Napraw połączenie {device.displayName}
						</DropdownMenuItem>
					),
				);
			}
		}
		if (connectionRemovable(connection)) {
			const reason = removeDeviceBlocker(device, view.removeMinAgentVersion);
			items.push(
				reason ? (
					<DisabledReason key="remove-conn" label="Usuń połączenie…" reason={reason} />
				) : (
					<DropdownMenuItem
						key="remove-conn"
						variant="destructive"
						onSelect={() => actions.openRemoveConnection(device, connection)}
					>
						Usuń połączenie z {device.displayName}…
					</DropdownMenuItem>
				),
			);
		}
	}

	if (items.length === 0) {
		items.push(
			<DisabledReason
				key="none"
				label="Brak akcji"
				reason={
					!device.online
						? "Komputer offline."
						: (fixAgentBlocker(device, view.fixMinAgentVersion) ?? "Ta pozycja nie ma akcji.")
				}
			/>,
		);
	}
	return (
		<>
			{node.installations.length > 1 ? (
				<DropdownMenuLabel className="text-[11px] font-normal text-muted-foreground">
					Na {device.displayName}
					{installation.isDefault ? " · domyślna" : ""}
				</DropdownMenuLabel>
			) : null}
			{items}
		</>
	);
}

/** Podmenu „Udostępnij także na…” / „Dodaj na stanowisko…” ze wszystkimi stanowiskami i powodami. */
function AssignSub({ node, ctx, label }: { node: PrinterNode; ctx: MapContext; label?: string }) {
	const { map, networks, view, actions } = ctx;
	const host = node.host;
	const cablePrinter = host
		? (node.installations.find((i) => i.link === "cable")?.printer ?? null)
		: null;
	const stations = map.stations.filter((s) => s.id !== host?.deviceUuid);
	if (stations.length === 0) return null;
	const title = label ?? (node.kind === "cable" ? "Udostępnij także na" : "Dodaj na stanowisko");
	return (
		<DropdownMenuSub>
			<DropdownMenuSubTrigger>
				<Plus className="size-3.5" aria-hidden />
				<span className="truncate">{title}</span>
			</DropdownMenuSubTrigger>
			<DropdownMenuSubContent className="max-w-72">
				{node.kind === "cable" ? (
					<DropdownMenuLabel className="text-[11px] font-normal whitespace-normal text-muted-foreground">
						Drukarka zostaje przy {host?.displayName} — stanowisko dostaje do niej połączenie.
					</DropdownMenuLabel>
				) : null}
				{stations.map((station) => {
					const reason = assignBlocker(node, station, networks, view.minAgentVersion);
					if (reason) {
						return (
							<DisabledReason key={station.id} label={station.device.displayName} reason={reason} />
						);
					}
					return (
						<DropdownMenuItem
							key={station.id}
							onSelect={() => assignToStation(node, station, cablePrinter, actions)}
						>
							{station.device.displayName}
						</DropdownMenuItem>
					);
				})}
			</DropdownMenuSubContent>
		</DropdownMenuSub>
	);
}

/**
 * Przypisanie drukarki stanowisku — ta sama droga z menu i z przeciągania.
 * Kablowa: udostępnienie do planu (z „ustaw jako domyślną”, do zmiany w planie);
 * sieciowa: okno dodania z zaznaczoną drukarką (wybór sterownika i nazwy).
 */
export function assignToStation(
	node: PrinterNode,
	station: StationNode,
	cablePrinter: PrinterItem | null,
	actions: MapActions,
) {
	if (node.kind === "cable" && node.host && cablePrinter) {
		actions.plan(
			{
				kind: "share",
				input: {
					hostDeviceUuid: node.host.deviceUuid,
					printerName: cablePrinter.name,
					clientDeviceUuid: station.id,
					setDefault: true,
				},
			},
			{
				host: node.host.displayName,
				client: station.device.displayName,
				printer: cablePrinter.name,
			},
		);
		return;
	}
	if (node.kind === "network" && node.address) {
		actions.openAdd(station.device, node.address);
	}
}

const TRIGGER_CLASS = "size-7 shrink-0 text-muted-foreground hover:text-foreground max-md:size-9";

/** Menu węzła drukarki: przypisanie, akcje każdej instalacji, adnotacje. */
export function PrinterNodeMenu({
	node,
	ctx,
	trigger,
}: {
	node: PrinterNode;
	ctx: MapContext;
	/** Własny wyzwalacz (cały węzeł na mapie); domyślnie przycisk „…”. */
	trigger?: ReactNode;
}) {
	const assignable = (node.kind === "cable" && ctx.sharing) || node.kind === "network";
	return (
		<DropdownMenu>
			<DropdownMenuTrigger asChild>
				{trigger ?? (
					<Button
						size="icon"
						variant="ghost"
						className={TRIGGER_CLASS}
						aria-label={`Akcje drukarki ${node.name}`}
					>
						<MoreHorizontal className="size-4" aria-hidden />
					</Button>
				)}
			</DropdownMenuTrigger>
			<DropdownMenuContent align="start" className="w-72">
				<DropdownMenuLabel className="space-y-0.5">
					<span className="block truncate">{node.name}</span>
					<span className="block truncate text-[11px] font-normal text-muted-foreground">
						{node.kindLabel}
					</span>
				</DropdownMenuLabel>
				{node.notes.length > 0 ? (
					<div className="space-y-1 px-2 pb-1.5 text-[11px] text-muted-foreground">
						{node.notes.map((note) => (
							<p key={note} className="flex items-start gap-1">
								<Info className="mt-0.5 size-3 shrink-0" aria-hidden />
								{note}
							</p>
						))}
					</div>
				) : null}
				{assignable ? (
					node.assignBlock ? (
						<DisabledReason
							label={node.kind === "cable" ? "Udostępnij także na…" : "Dodaj na stanowisko…"}
							reason={node.assignBlock}
						/>
					) : (
						<AssignSub node={node} ctx={ctx} />
					)
				) : null}
				{node.installations.map((installation) => (
					<div
						key={`${installation.device.deviceUuid}:${installation.printer?.name ?? installation.connection?.path}`}
					>
						<DropdownMenuSeparator />
						<InstallationItems node={node} installation={installation} ctx={ctx} />
					</div>
				))}
				{node.host ? (
					<>
						<DropdownMenuSeparator />
						<DropdownMenuItem onSelect={() => node.host && ctx.actions.openDetails(node.host)}>
							Szczegóły komputera {node.host.displayName}
						</DropdownMenuItem>
					</>
				) : null}
			</DropdownMenuContent>
		</DropdownMenu>
	);
}

/** Menu stanowiska: dodanie drukarki sieciowej, domyślna, bufor, szczegóły. */
export function StationMenu({
	station,
	ctx,
	trigger,
}: {
	station: StationNode;
	ctx: MapContext;
	trigger?: ReactNode;
}) {
	const { device } = station;
	const { actions, view, map, sharing } = ctx;
	// Drukarki na kablu tego komputera — z menu stanowiska też da się je udostępnić innym.
	const hosted = map.printers.filter(
		(p) => p.kind === "cable" && p.host?.deviceUuid === device.deviceUuid,
	);
	const addBlock = addDeviceBlocker(device, view.addMinAgentVersion);
	const fixBlock = !device.online
		? "Komputer offline."
		: device.supported
			? fixAgentBlocker(device, view.fixMinAgentVersion)
			: "Komputer bez obsługi modułu (starszy agent albo brak danych o drukarkach).";
	return (
		<DropdownMenu>
			<DropdownMenuTrigger asChild>
				{trigger ?? (
					<Button
						size="icon"
						variant="ghost"
						className={TRIGGER_CLASS}
						aria-label={`Akcje stanowiska ${device.displayName}`}
					>
						<MoreHorizontal className="size-4" aria-hidden />
					</Button>
				)}
			</DropdownMenuTrigger>
			<DropdownMenuContent align="start" className="w-72">
				<DropdownMenuLabel className="space-y-0.5">
					<span className="block truncate">{device.displayName}</span>
					<span className="block truncate text-[11px] font-normal text-muted-foreground">
						{[
							device.online ? "online" : "offline",
							device.serviceVersion ? `agent ${device.serviceVersion}` : null,
						]
							.filter(Boolean)
							.join(" · ")}
					</span>
				</DropdownMenuLabel>
				{station.assignBlock ? (
					<p className="px-2 pb-1.5 text-[11px] text-muted-foreground">{station.assignBlock}</p>
				) : null}
				<DropdownMenuSeparator />
				{sharing ? (
					hosted.length === 0 ? (
						<DisabledReason
							label="Udostępnij drukarkę na inne stanowiska…"
							reason="Ten komputer nie ma drukarki na kablu (USB/LPT). Drukarki sieciowe dodaje się na każdym stanowisku osobno."
						/>
					) : (
						hosted.map((node) =>
							node.assignBlock ? (
								<DisabledReason
									key={node.id}
									label={`Udostępnij „${node.name}” na…`}
									reason={node.assignBlock}
								/>
							) : (
								<AssignSub
									key={node.id}
									node={node}
									ctx={ctx}
									label={`Udostępnij „${node.name}” na`}
								/>
							),
						)
					)
				) : null}
				{addBlock ? (
					<DisabledReason label="Dodaj drukarkę sieciową…" reason={addBlock} />
				) : (
					<DropdownMenuItem onSelect={() => actions.openAdd(device)}>
						<Plus className="size-3.5" aria-hidden />
						Dodaj drukarkę sieciową…
					</DropdownMenuItem>
				)}
				{fixBlock ? (
					<DisabledReason label="Drukarka domyślna…" reason={fixBlock} />
				) : (
					<>
						<DropdownMenuItem onSelect={() => actions.openDefault(device)}>
							<Star className="size-3.5" aria-hidden />
							Drukarka domyślna…
						</DropdownMenuItem>
						<DropdownMenuItem
							variant="destructive"
							onSelect={() =>
								actions.plan(
									{ kind: "reset_spooler", deviceUuid: device.deviceUuid },
									{ device: device.displayName },
								)
							}
						>
							<RotateCcw className="size-3.5" aria-hidden />
							Zresetuj bufor wydruku
						</DropdownMenuItem>
					</>
				)}
				<DropdownMenuSeparator />
				<DropdownMenuItem onSelect={() => actions.openDetails(device)}>
					Szczegóły i wszystkie drukarki…
				</DropdownMenuItem>
			</DropdownMenuContent>
		</DropdownMenu>
	);
}
